'use strict';

import { flow, observable, action, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import appStore from '@uw-it-sis/lib-react/lib/AppStore';

import NotifyUtils from './NotifyUtils';
import {isEmpty}from '@uw-it-sis/lib-react/lib/Utils';

/**
 * Storage for notification related data.
 */
class NotifyStore {

    state = States.initial;
    subscribeFormActivityId = null;

    constructor() {
        makeObservable(this, {
            state: observable,
            subscribeFormActivityId: observable,
            viewSectionSubscribeForm: action,
            hideSectionSubscribeForm: action
        });

        this.baseUrl = `api/notify`;

        this.notifyData = {};

        // Data object for all the user subscriptions
        this.subscriptions = {};

        // Data object for all the user endpoints
        this.endpoints = {};
    }

    async getProfile() {
        let url = `${this.baseUrl}/profile`;
        return await appStore.getHttpClient().doGet(url, 'getProfile');
    }

    async getSubscriptions() {
        let url = `${this.baseUrl}/profile/subscriptions`;
        return await appStore.getHttpClient().doGet(url, 'getSubscriptions');
    }

    /**
     * Loads the Notify Details which contains all subscriptions and endpoints
     */
    loadNotifyData = flow(function* (includeSubscriptions = false) {
        if (appStore.isDebug()) {
            console.log("NotifyStore: Starting data load.");
        }

        // Update the aggregate state to pending.
        this.state = States.pending;

        try {
            this.notifyData.profile = yield this.getProfile();

            if (includeSubscriptions) {
                this.notifyData.subscriptions = yield this.getSubscriptions();
            }

            if (appStore.isDebug()) console.log("NotifyStore: Data load complete.");

            this.updateNotifyData();
            this.state = States.done;
        } catch (error) {
            appStore.getExceptionUtils().handleException(error);
            this.state = States.error;
        }
    }.bind(this));

    /**
     * Indicate that the subscribe form for the specified activityId should be shown.
     */
    viewSectionSubscribeForm(activityId) {
        this.subscribeFormActivityId = activityId;

    }

    /**
     * Hide any subscribe form that's currently shown
     */
    hideSectionSubscribeForm() {
        this.subscribeFormActivityId = null;
    }

    /**
     * Populates the store data objects for accessing subscriptions and endpoints
     */
    updateNotifyData() {
        //  Clear out the existing data.
        this.subscriptions = {};
        this.endpoints = {};

        if (this.notifyData.profile.hasOwnProperty("Person")) {
            // Map end-points.
            this.notifyData.profile.Person.Endpoints.map(function (Endpoint) {
                this.endpoints[Endpoint.Protocol] = Endpoint;
            }, this);
        }

        // Map subscriptions
        if (this.notifyData.hasOwnProperty("subscriptions") && this.notifyData.subscriptions.hasOwnProperty("Subscriptions")) {
            this.notifyData.subscriptions.Subscriptions.map(function (subscription) {
                if (this.subscriptions[subscription.Channel.SurrogateID]) {
                    this.subscriptions[subscription.Channel.SurrogateID].push(subscription.Endpoint);
                } else {
                    this.subscriptions[subscription.Channel.SurrogateID] = [subscription.Endpoint];
                }
            }, this);
        }
    }

    /**
     * Returns true if the data is available. Otherwise, false.
     */
    hasNotifyData() {
        return ! isEmpty(this.notifyData);
    }

    /**
     * Should we show the subscribe form for this activityId?
     */
    sectionSubscribeFormShown(activityId) {
        return this.subscribeFormActivityId === activityId;
    }

    /**
     * Returns true if the user has endpoints setup in the UW.Notify settings
     */
    hasEndPoints () {
        return ! isEmpty(this.endpoints);
    }

    /**
     * Returns the endpoint data object for the specified protocol
     */
    getEndPoint(protocol) {
        return this.endpoints[protocol];
    }

    /**
     * Returns the subscription data object for a specified activityId
     */
    getSubscription(activityId) {
        return this.subscriptions[NotifyUtils.getSurrogateIdForActivityId(activityId)];
    }

    /**
     * Returns true if the specified activityId is subscribed to.
     */
    isSubscribed(activityId) {
        return NotifyUtils.getSurrogateIdForActivityId(activityId) in this.subscriptions;
    }

    /**
     * Returns true if the specified activityId has an endPoint subscription with the specified protocol
     */
    isEndPointSubscribed(activityId, protocol) {
        let returnValue = false;
        let surrogateId = NotifyUtils.getSurrogateIdForActivityId(activityId);
        if (this.getSubscription(surrogateId)) {
            this.getSubscription(surrogateId).map(function(endPoint) {
                if (endPoint.Protocol === protocol) {
                    returnValue = true;
                }
            });
        }
        return returnValue;
    }
}

export default new NotifyStore();